import React, { useMemo, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Tooltip from 'components_new/atoms/Tooltip'

import FilterPopover from 'components_new/molecules/FilterPopover'
import SearchAndSelect from 'components_new/molecules/SearchAndSelect'

import { useTranslation } from 'language/translator'

import { useAnalysisActions } from 'redux/actions/Analyses'
import { TAnalysis } from 'redux/hooks/Analyses'

import { EAnalysisStatus } from 'types/Enums'

/**
 * Lets the customer reshape which real estates ("mätobjekt") are
 * included in a dynamic analysis. Changes are kept in a temporary
 * selection until saved — same cancel/save pattern as the global
 * filter. Saving persists against the analysis, and the response
 * carries a fresh html url with the new selection signed in, which
 * reloads the iframe.
 */
const AnalysisObjectSelection = ({ analysis }: { analysis: TAnalysis }) => {
  const { t, translateEnum } = useTranslation()
  const { tryUpdateAnalysisSelection } = useAnalysisActions()

  const savedNames = useMemo(
    () =>
      analysis.qualified_properties
        .filter((property) => property.selected)
        .map((property) => property.name),
    [analysis.properties]
  )

  // Local temporary state (for editing before save)
  const [open, setOpen] = useState<boolean>(false)
  const [tempSelected, setTempSelected] = useState<string[]>(savedNames)

  const { status } = analysis

  const locked = status !== EAnalysisStatus.UNHANDLED
  const lockedTooltip = locked
    ? t('analyses', 'selection_locked_tooltip', false, {
      status: translateEnum('analysis_status', status)
    })
    : ''

  const options = analysis.qualified_properties.map((property) => ({
    id: property.name,
    title: property.name
  }))

  const getValueLabel = () => {
    if (savedNames.length === 0) {
      return t('global', 'none')
    }

    if (savedNames.length === analysis.qualified_properties.length) {
      return t('global', 'all')
    }

    return savedNames.join(', ')
  }

  const valueLabel = getValueLabel()

  const handleClick = () => {
    // Reset temporary state to saved values when opening
    setTempSelected(savedNames)
    setOpen(true)
  }

  const handleClose = () => {
    // Reset temporary state to saved values on cancel
    setTempSelected(savedNames)
    setOpen(false)
  }

  const handleSave = () => {
    tryUpdateAnalysisSelection(analysis.id, tempSelected)
    setOpen(false)
  }

  return (
    <Tooltip title={locked ? lockedTooltip : ''}>
      <span>
        <FilterPopover
          active={savedNames.length !== analysis.qualified_properties.length}
          disabled={locked}
          onClick={handleClick}
          onClose={handleClose}
          open={open}
          popoverWidth={300}
          position="top"
          primary={t('analyses', 'selection')}
          secondary={valueLabel}
          showActive={true}
          size="medium"
          sx={{ maxWidth: 240 }}
          tooltipTitle={t('analyses', 'selection')}
          tooltipValue={valueLabel}
        >
          <SearchAndSelect
            autoFocus={true}
            multiple={true}
            onChange={setTempSelected}
            options={options}
            size="small"
            sx={{ p: 2 }}
            value={tempSelected}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end',
              p: 1,
              pt: 0
            }}
          >
            <Button onClick={handleClose} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button onClick={handleSave}>{t('global', 'save')}</Button>
          </Box>
        </FilterPopover>
      </span>
    </Tooltip>
  )
}

export default AnalysisObjectSelection
